import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../css/GalleryCards.css";
import AOS from 'aos';
import { motion } from "framer-motion"; 
import 'aos/dist/aos.css';
import { 
  FaLeaf, 
  FaGem,
  FaArrowRight,
  FaWarehouse,
  FaHandshake,
  FaTags,
  FaTruck,
  FaIndustry,
  FaStore,
  FaBoxes,
  FaClipboardCheck,
  FaShippingFast,
  FaRecycle,
  FaCheckCircle
} from 'react-icons/fa';

// Company Services Data
const companyServices = [
  {
    id: 'wholesale-supply',
    title: 'Wholesale Supply',
    description: 'Bulk supply of premium nutraceutical products for retailers, distributors, and businesses across India.',
    icon: <FaWarehouse />,
    color: '#2E7D32',
    features: ['Competitive wholesale pricing', 'Bulk order discounts', 'Pan India delivery', 'Consistent quality assurance']
  },
  {
    id: 'retail-partnership',
    title: 'Retail Partnership',
    description: 'Partner with TerraGold to stock and sell our premium organic products in your retail stores.',
    icon: <FaStore />,
    color: '#43A047',
    features: ['Retailer support program', 'Shelf-ready premium packaging', 'Marketing & display materials', 'Fast order fulfillment']
  },
  {
    id: 'private-label',
    title: 'Private Label Manufacturing',
    description: 'Create your own brand with our premium nutraceutical products. Custom packaging and labeling solutions.',
    icon: <FaTags />,
    color: '#FFA726',
    features: ['Custom branding & labeling', 'Flexible minimum order quantity', 'Product formulation options', 'Quality assurance certified']
  },
  {
    id: 'bulk-manufacturing',
    title: 'Bulk Manufacturing',
    description: 'State-of-the-art contract manufacturing facility for large-scale production of nutraceutical products.',
    icon: <FaIndustry />,
    color: '#00897B',
    features: ['GMP & FSSAI certified facility', 'Quality control testing', 'Large-scale production capacity', 'Timely delivery commitment']
  },
  {
    id: 'distribution',
    title: 'Distribution Services',
    description: 'Comprehensive distribution network ensuring timely delivery of products across India.',
    icon: <FaTruck />,
    color: '#6A1B9A',
    features: ['Pan India coverage', 'Temperature-controlled logistics', 'Real-time shipment tracking', 'Warehousing facilities']
  },
  {
    id: 'corporate-gifting',
    title: 'Corporate Gifting Solutions',
    description: 'Premium wellness gift solutions for corporate clients, employees, and business partners.',
    icon: <FaHandshake />,
    color: '#FF6F00',
    features: ['Customized gift packaging', 'Premium product selection', 'Bulk order handling', 'Timely delivery guaranteed']
  },
  {
    id: 'export-services',
    title: 'Export Services',
    description: 'Quality nutraceutical products exported to international markets meeting global standards.',
    icon: <FaShippingFast />,
    color: '#1565C0',
    features: ['International quality standards', 'Export documentation support', 'Global shipping', 'Customs clearance assistance']
  },
  {
    id: 'contract-manufacturing',
    title: 'Contract Manufacturing',
    description: 'End-to-end manufacturing solutions for nutraceutical products under your brand name.',
    icon: <FaClipboardCheck />,
    color: '#4CAF50',
    features: ['Complete manufacturing support', 'Quality testing & certification', 'Packaging solutions', 'Supply chain management']
  }
];

function Service() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredServices, setFilteredServices] = useState(companyServices);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: 'ease-out-cubic',
      once: false,
      mirror: true,
      offset: 100
    });
  }, []);

  // Filter options
  const filterOptions = [
    { id: 'all', label: 'All Services' },
    { id: 'supply', label: 'Supply & Distribution' },
    { id: 'manufacturing', label: 'Manufacturing' },
    { id: 'business', label: 'Business Solutions' }
  ];

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredServices(companyServices);
    } else if (activeFilter === 'supply') {
      setFilteredServices(companyServices.filter(s => 
        s.id === 'wholesale-supply' || s.id === 'distribution' || s.id === 'export-services'
      ));
    } else if (activeFilter === 'manufacturing') {
      setFilteredServices(companyServices.filter(s => 
        s.id === 'private-label' || s.id === 'bulk-manufacturing' || s.id === 'contract-manufacturing'
      ));
    } else if (activeFilter === 'business') {
      setFilteredServices(companyServices.filter(s => 
        s.id === 'retail-partnership' || s.id === 'corporate-gifting'
      ));
    }
  }, [activeFilter]);

  return (
    <div className="premium-services-page">
      {/* Animated Hero Section */}
      <div className="premium-hero">
        <div className="premium-hero-bg"></div>
        <div className="premium-hero-particles">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="hero-particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}></div>
          ))}
        </div>
        <div className="premium-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-floating-badge"
          >
            <FaGem className="badge-gem" /> TerraGold Services
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="premium-hero-title"
          >
            <span className="gradient-text-green">Our</span>
            <span className="gradient-text-gold">Services</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="premium-hero-subtitle"
          >
            Comprehensive Nutraceutical Solutions — From Manufacturing to Distribution
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="hero-cta-group"
          >
            <button className="hero-cta-primary">Explore Services</button>
            <button className="hero-cta-secondary">Contact Sales →</button>
          </motion.div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Floating Stats Bar */}
      <div className="floating-stats">
        <div className="stat-item">
          <span className="stat-number">500+</span>
          <span className="stat-label">BUSINESS PARTNERS</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-number">50+</span>
          <span className="stat-label">PRODUCTS</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-number">PAN INDIA</span>
          <span className="stat-label">DISTRIBUTION</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-number">✓</span>
          <span className="stat-label">GMP & FSSAI</span>
        </div>
      </div>

      {/* Services Section */}
      <div className="premium-services-section">
        <div className="section-header-premium">
          <span className="section-chip"><FaGem className="chip-icon" /> What We Offer</span>
          <h2>Our <span className="gradient-text2">Services</span></h2>
          <p>Comprehensive nutraceutical solutions tailored to your business needs</p>
        </div>

        {/* Filter Buttons */}
        <div className="service-filter-buttons">
          {filterOptions.map(option => (
            <button
              key={option.id}
              className={`filter-btn ${activeFilter === option.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="services-grid">
          {filteredServices.map((service, index) => (
            <motion.div
              key={service.id}
              className="service-card"
              data-aos="fade-up"
              data-aos-delay={index * 100}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ 
                y: -10,
                boxShadow: `0 20px 50px ${service.color}25`
              }}
              style={{ '--card-color': service.color }}
            >
              <div className="service-icon-wrapper" style={{ background: service.color }}>
                {service.icon}
              </div>
              <div className="service-content">
                <h3>{service.title}</h3>
                <p style={{textAlign:"left"}}>{service.description}</p>
                <div className="service-features">
                  {service.features.map((feature, i) => (
                    <span key={i} className="service-feature">
                      <FaCheckCircle /> {feature}
                    </span>
                  ))}
                </div>
                <Link to="/contact" className="service-cta">
                  Enquire Now <FaArrowRight />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Why Choose TerraGold Services */}
      <div className="why-choose-services">
        <div className="container">
          <div className="section-header-premium">
            <span className="section-chip">Why TerraGold</span>
            <h2>Why Choose <span className="gradient-text2">Our Services</span></h2>
            <p>Partner with us for quality, trust, and excellence</p>
          </div>

          <div className="why-grid">
            <div className="why-card" data-aos="flip-left">
              <div className="why-icon" style={{ background: '#2E7D32' }}>
                <FaIndustry />
              </div>
              <h4>Quality Manufacturing</h4>
              <p style={{textAlign:"left"}}>GMP & FSSAI certified facilities ensuring premium quality products</p>
            </div>
            <div className="why-card" data-aos="flip-left" data-aos-delay="100">
              <div className="why-icon" style={{ background: '#FFA726' }}>
                <FaTags />
              </div>
              <h4>Custom Solutions</h4>
              <p style={{textAlign:"left"}}>Private label and custom formulation services for your brand</p>
            </div>
            <div className="why-card" data-aos="flip-left" data-aos-delay="200">
              <div className="why-icon" style={{ background: '#43A047' }}>
                <FaTruck />
              </div>
              <h4>Pan India Supply</h4>
              <p style={{textAlign:"left"}}>Reliable distribution network across India with timely delivery</p>
            </div>
            <div className="why-card" data-aos="flip-left" data-aos-delay="300">
              <div className="why-icon" style={{ background: '#6A1B9A' }}>
                <FaHandshake />
              </div>
              <h4>Trusted Partner</h4>
              <p style={{textAlign:"left"}}>500+ business partners trust us for their nutraceutical needs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="services-cta-section">
        <div className="services-cta-overlay"></div>
        <div className="container">
          <div className="services-cta-content">
            <h2>Ready to <span className="gradient-text-gold">Partner</span> With Us?</h2>
            <p>Get in touch with our team to discuss your business requirements</p>
            <div className="services-cta-buttons">
              <Link to="/contact" className="cta-primary-btn">
                <FaHandshake /> Contact Sales Team
              </Link>
              <Link to="/products" className="cta-secondary-btn">
                <FaLeaf /> Explore Products
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Service;